import { useEffect, useRef, useState } from "react";
import type { SyntheticEvent } from "react";
import { FileText, Paperclip, Send, X } from "lucide-react";
import { feedbackApi, type FeedbackAttachmentInput } from "../../api";
import { Button, Modal } from "../ui";
import {
  FEEDBACK_ACCEPT,
  MAX_ATTACHMENTS,
  MAX_ATTACHMENT_BYTES,
  fileToAttachment,
  formatBytes,
  formatNumber,
  isAllowedFile,
} from "../../utils";

type FeedbackModalProps = {
  messageId: string;
  onClose: () => void;
  onSubmitted?: () => void;
};

const MAX_MB = MAX_ATTACHMENT_BYTES / (1024 * 1024);

export default function FeedbackModal({
  messageId,
  onClose,
  onSubmitted,
}: FeedbackModalProps) {
  const [comment, setComment] = useState("");
  const [attachments, setAttachments] = useState<FeedbackAttachmentInput[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const commentRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = commentRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(Math.max(el.scrollHeight, 72), 200)}px`;
  }, [comment]);

  async function addFiles(files: File[]) {
    if (!files.length) return;
    setError(null);

    let added = attachments.length;
    const accepted: FeedbackAttachmentInput[] = [];
    for (const file of files) {
      if (added >= MAX_ATTACHMENTS) {
        setError(`You can attach up to ${MAX_ATTACHMENTS} files.`);
        break;
      }
      if (!isAllowedFile(file)) {
        setError(
          `"${file.name}" is not an allowed type. Allowed: images, PDF, CSV, TXT, Word docs.`,
        );
        continue;
      }
      if (file.size > MAX_ATTACHMENT_BYTES) {
        setError(`"${file.name}" is larger than ${MAX_MB}MB.`);
        continue;
      }
      try {
        accepted.push(await fileToAttachment(file));
        added += 1;
      } catch {
        setError(`Could not read "${file.name}".`);
      }
    }
    if (accepted.length) {
      setAttachments((prev) => [...prev, ...accepted]);
    }
  }

  async function handleFiles(event: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    await addFiles(files);
  }

  function handlePaste(event: React.ClipboardEvent<HTMLTextAreaElement>) {
    const images: File[] = [];
    for (const item of Array.from(event.clipboardData.items)) {
      if (item.kind === "file" && item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file) {
          const named = file.name
            ? file
            : new File([file], `screenshot-${images.length + 1}.png`, {
                type: file.type || "image/png",
              });
          images.push(named);
        }
      }
    }
    if (images.length) {
      event.preventDefault();
      void addFiles(images);
    }
  }

  function removeAttachment(index: number) {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleSubmit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmedComment = comment.trim();
    if (!trimmedComment) {
      setError("Please add a short comment so admins know what to check.");
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      await feedbackApi.submit({
        messageId,
        comment: trimmedComment,
        attachments,
      });
      setIsSubmitted(true);
      onSubmitted?.();
    } catch {
      return;
    } finally {
      setIsSubmitting(false);
    }
  }

  const canAttach = attachments.length < MAX_ATTACHMENTS;

  return (
    <Modal
      onClose={onClose}
      size="md"
      title="Send feedback"
      subtitle={
        isSubmitted
          ? undefined
          : "Tell admins what looks wrong or needs checking."
      }
      onSubmit={isSubmitted ? undefined : handleSubmit}
      footer={
        isSubmitted ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !comment.trim()}>
              <Send size={14} />
              {isSubmitting ? "Sending..." : "Send"}
            </Button>
          </>
        )
      }
    >
      {isSubmitted ? (
        <p className="text-sm text-gray-700">
          Thanks, this feedback has been sent to admins.
        </p>
      ) : (
        <div className="space-y-4">
          <div>
            <label
              htmlFor="feedback-comment"
              className="mb-1 block text-xs font-semibold text-gray-700"
            >
              Comment
            </label>
            <textarea
              ref={commentRef}
              id="feedback-comment"
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              onPaste={handlePaste}
              rows={1}
              maxLength={4000}
              placeholder="Example: this total looks higher than Power BI for the same period. Paste a screenshot."
              className="block max-h-[200px] w-full resize-none overflow-y-auto rounded-xl border border-gray-300 bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <p className="mt-1 text-right text-[11px] text-gray-400">
              {formatNumber(comment.length)} / 4,000
            </p>
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-700">
                Attachments
              </span>
              <span className="text-[11px] text-gray-400">
                Images, PDF, CSV, TXT, Word · max {MAX_MB}MB each · up to{" "}
                {MAX_ATTACHMENTS}
              </span>
            </div>

            {attachments.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-2">
                {attachments.map((att, idx) => {
                  const isImage = att.content_type.startsWith("image/");
                  return (
                    <div key={idx} className="relative shrink-0">
                      {isImage ? (
                        <img
                          src={`data:${att.content_type};base64,${att.data}`}
                          alt={att.filename}
                          className="h-16 w-16 rounded-lg border border-gray-200 object-cover"
                        />
                      ) : (
                        <div className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs text-gray-700">
                          <FileText
                            size={12}
                            className="shrink-0 text-gray-500"
                          />
                          <span className="max-w-[160px] truncate font-medium">
                            {att.filename}
                          </span>
                          <span className="text-gray-400">
                            {formatBytes(att.size_bytes)}
                          </span>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => removeAttachment(idx)}
                        className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-gray-600 text-white hover:bg-gray-800"
                        aria-label={`Remove ${att.filename}`}
                      >
                        <X size={9} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept={FEEDBACK_ACCEPT}
              multiple
              className="hidden"
              onChange={handleFiles}
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={!canAttach}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Paperclip size={13} />
                Add files
              </button>
              <span className="text-[11px] text-gray-400">
                or paste a screenshot
              </span>
            </div>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
      )}
    </Modal>
  );
}
